<template>
  <v-container class="grey-lighten-3 full-height d-flex flex-column">
    <!-- Loading State -->
    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading Shipment details...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-else-if="!shipmentData || Object.keys(shipmentData).length === 0" justify="center">
      <v-col cols="12" class="text-center">
        <p class="text-grey">No Shipment data found</p>
        <v-btn color="primary" @click="goBack" class="mt-4">Go Back</v-btn>
      </v-col>
    </v-row>

    <!-- Printable Area -->
    <v-row v-else justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <div class="print-container" id="printable-area">
          <!-- Header Section -->
          <div class="header">
            <img
              v-if="getCurrentCompany?.logo_thumb"
              :src="`${getFileServerUrl.replace(/\/$/, '')}/${getCurrentCompany.logo_thumb}`"
              alt="Company Logo"
              class="company-logo"
            />
            <div v-else class="logo-placeholder">
              <v-icon icon="mdi-domain" size="60" color="grey-darken-2"></v-icon>
            </div>
            <div class="company-info">
              <strong>{{ getCurrentCompany?.name || 'Company Name' }}</strong><br>
              {{ getCurrentCompany?.address || 'Company Address' }}<br>
              <span v-if="getCurrentCompany?.email">Email: {{ getCurrentCompany.email }}</span>
              <span v-if="getCurrentCompany?.mobile"> | Phone: {{ getCurrentCompany.mobile }}</span>
            </div>
          </div>

          <!-- Shipment Title -->
          <div class="lc-header-info">
            <h2 class="lc-title">LC IMPORT SHIPMENT</h2>
            <p class="lc-meta">
              <strong>Shipment No:</strong> {{ shipmentData.shipment_no || 'N/A' }} | 
              <strong>Shipment Code:</strong> {{ shipmentData.code || 'N/A' }} |
              <strong>Shipment Date:</strong> {{ formatDate(shipmentData.shipment_date) }}
            </p>
          </div>

          <!-- Shipment Information Section -->
          <div class="section-title">Shipment Information</div>
          <div class="info-section">
            <table class="info-table">
              <tbody>
                <tr>
                  <td class="info-label"><strong>LC No:</strong></td>
                  <td class="info-value">{{ shipmentData.lc_master?.lc_no || shipmentData.lcMaster?.lc_no || shipmentData.lc_master?.code || shipmentData.lcMaster?.code || 'N/A' }}</td>
                  <td class="info-label"><strong>Shipment Status:</strong></td>
                  <td class="info-value">
                    <v-chip size="small" :color="getShipmentStatusColor(shipmentData.shipment_status)">
                      {{ shipmentData.shipment_status || 'Draft' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="info-label"><strong>ETD:</strong></td>
                  <td class="info-value">{{ shipmentData.etd ? formatDate(shipmentData.etd) : 'N/A' }}</td>
                  <td class="info-label"><strong>ETA:</strong></td>
                  <td class="info-value">{{ shipmentData.eta ? formatDate(shipmentData.eta) : 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Loading Port:</strong></td>
                  <td class="info-value">{{ shipmentData.loading_port?.name || 'N/A' }}</td>
                  <td class="info-label"><strong>Discharge Port:</strong></td>
                  <td class="info-value">{{ shipmentData.discharge_port?.name || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Carrier:</strong></td>
                  <td class="info-value">{{ shipmentData.carrier || 'N/A' }}</td>
                  <td class="info-label"><strong>BL No:</strong></td>
                  <td class="info-value">{{ shipmentData.bl_no || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Container No:</strong></td>
                  <td class="info-value">{{ shipmentData.container_no || 'N/A' }}</td>
                  <td class="info-label"><strong>Document Status:</strong></td>
                  <td class="info-value">
                    <v-chip size="small" :color="getDocumentStatusColor(shipmentData.document_status)">
                      {{ shipmentData.document_status || 'PENDING' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Shipment Value:</strong></td>
                  <td class="info-value"><strong>{{ formatCurrency(shipmentData.shipment_value) }}</strong></td>
                  <td class="info-label"><strong>Approval Status:</strong></td>
                  <td class="info-value">
                    <v-chip size="small" :color="getApprovalStatusColor(shipmentData.approval_status)">
                      {{ shipmentData.approval_status || 'Pending' }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="shipmentData.remarks">
                  <td class="info-label"><strong>Remarks:</strong></td>
                  <td class="info-value" colspan="3">{{ shipmentData.remarks }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- LC Information Section -->
          <div v-if="shipmentData.lc_master || shipmentData.lcMaster" class="section-title">LC Information</div>
          <div v-if="shipmentData.lc_master || shipmentData.lcMaster" class="info-section">
            <table class="info-table">
              <tbody>
                <tr>
                  <td class="info-label"><strong>LC No:</strong></td>
                  <td class="info-value">{{ (shipmentData.lc_master || shipmentData.lcMaster)?.lc_no || 'N/A' }}</td>
                  <td class="info-label"><strong>LC Code:</strong></td>
                  <td class="info-value">{{ (shipmentData.lc_master || shipmentData.lcMaster)?.code || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>Supplier:</strong></td>
                  <td class="info-value">{{ (shipmentData.lc_master || shipmentData.lcMaster)?.supplier?.supplier_name || 'N/A' }}</td>
                  <td class="info-label"><strong>LC Amount:</strong></td>
                  <td class="info-value">{{ formatCurrency((shipmentData.lc_master || shipmentData.lcMaster)?.lc_amount || 0) }}</td>
                </tr>
                <tr>
                  <td class="info-label"><strong>LC Status:</strong></td>
                  <td class="info-value">{{ (shipmentData.lc_master || shipmentData.lcMaster)?.lc_status || 'N/A' }}</td>
                  <td class="info-label"><strong>Currency:</strong></td>
                  <td class="info-value">{{ (shipmentData.lc_master || shipmentData.lcMaster)?.currency?.code || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Shipment Items Section -->
          <div class="section-title">Shipment Items</div>
          <div class="pi-items-section">
            <table class="items-table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Product</th>
                  <th>Product Code</th>
                  <th>UOM</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Rate</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, itemIndex) in (shipmentData.shipment_items || shipmentData.shipmentItems || [])" :key="itemIndex">
                  <td>{{ itemIndex + 1 }}</td>
                  <td>{{ item.product?.name || 'N/A' }}</td>
                  <td>{{ item.product?.code || 'N/A' }}</td>
                  <td>{{ item.uom || item.product?.unit?.name || 'PCS' }}</td>
                  <td class="text-right">{{ formatNumber(item.received_qty || item.qty) }}</td>
                  <td class="text-right">{{ formatNumber(item.rate) }}</td>
                  <td class="text-right">{{ formatNumber(item.amount) }}</td>
                </tr>
                <tr v-if="(!shipmentData.shipment_items || shipmentData.shipment_items.length === 0) && (!shipmentData.shipmentItems || shipmentData.shipmentItems.length === 0)">
                  <td colspan="7" class="text-center text-grey">No items found</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Shipment Summary -->
          <div class="lc-summary-section">
            <div class="section-title">Shipment Summary</div>
            <table class="summary-table">
              <tbody>
                <tr>
                  <td><strong>Total Items:</strong></td>
                  <td class="text-right">{{ (shipmentData.shipment_items || shipmentData.shipmentItems || []).length }}</td>
                </tr>
                <tr>
                  <td><strong>Total Shipment Value:</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(shipmentData.shipment_value || 0) }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Action Buttons -->
        <v-row class="mt-4 mb-4">
          <v-col cols="12" class="text-right">
            <v-btn color="primary" @click="printDocument" prepend-icon="mdi-printer">
              Print
            </v-btn>
            <v-btn color="grey" variant="outlined" @click="goBack" class="ml-2">
              Go Back
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formatDateView from "@/plugins/dateFormatView";
import html2pdf from "html2pdf.js";

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const loading = ref(false);
    const shipmentData = ref(null);

    const getCurrentCompany = computed(() => {
      return store.getters.getCurrentCompany || {};
    });

    const getFileServerUrl = computed(() => {
      return store.getters.getFileServerUrl || "";
    });

    const loadShipmentData = async () => {
      const shipmentId = route.params.id;
      if (!shipmentId) {
        toast.error("Shipment ID is required");
        router.push({ name: "lc-import-lc-shipment-records" });
        return;
      }

      loading.value = true;
      try {
        const data = await store.dispatch("getLcImportLcShipmentById", shipmentId);
        shipmentData.value = data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load shipment data");
        router.push({ name: "lc-import-lc-shipment-records" });
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return "0.00";
      const lcMaster = shipmentData.value?.lc_master || shipmentData.value?.lcMaster;
      const currencyCode = lcMaster?.currency?.symbol || "৳";
      return `${currencyCode} ${Number(amount).toFixed(2)}`;
    };

    const getShipmentStatusColor = (status) => {
      const colors = {
        Draft: "grey",
        Shipped: "info",
        Arrived: "primary",
        Cleared: "success",
        Closed: "default",
      };
      return colors[status] || "grey";
    };

    const getDocumentStatusColor = (status) => {
      const colors = {
        PENDING: "warning",
        RECEIVED: "info",
        MISMATCH: "error",
        REJECTED: "error",
      };
      return colors[status] || "warning";
    };

    const getApprovalStatusColor = (status) => {
      const colors = {
        Pending: "warning",
        Approved: "success",
        Cancelled: "error",
      };
      return colors[status] || "warning";
    };

    const printDocument = () => {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: 0.5,
        filename: `Shipment_${shipmentData.value?.code || shipmentData.value?.shipment_no || "document"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      };
      html2pdf().set(opt).from(element).save();
    };

    const goBack = () => {
      router.push({ name: "lc-import-lc-shipment-records" });
    };

    onMounted(() => {
      loadShipmentData();
    });

    return {
      loading,
      shipmentData,
      getCurrentCompany,
      getFileServerUrl,
      formatDate,
      formatNumber,
      formatCurrency,
      getShipmentStatusColor,
      getDocumentStatusColor,
      getApprovalStatusColor,
      printDocument,
      goBack,
    };
  },
};
</script>

<style scoped>
.full-height {
  min-height: 100vh;
}

.print-container {
  background: white;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.company-logo {
  max-width: 120px;
  max-height: 120px;
  margin-right: 20px;
}

.logo-placeholder {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  background: #f5f5f5;
  border-radius: 4px;
}

.company-info {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
}

.lc-header-info {
  text-align: center;
  margin-bottom: 30px;
}

.lc-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #1976d2;
}

.lc-meta {
  font-size: 14px;
  color: #666;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  margin: 30px 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #1976d2;
  color: #1976d2;
}

.section-title-small {
  font-size: 14px;
  font-weight: bold;
  margin: 15px 0 10px 0;
  color: #666;
}

.info-section {
  margin-bottom: 20px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
}

.info-table td {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
}

.info-label {
  width: 25%;
  background-color: #f5f5f5;
  font-weight: 500;
}

.info-value {
  width: 25%;
}

.pi-items-section {
  margin: 20px 0;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.items-table th,
.items-table td {
  padding: 10px;
  border: 1px solid #e0e0e0;
  text-align: left;
}

.items-table th {
  background-color: #1976d2;
  color: white;
  font-weight: bold;
}

.items-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.pi-summary {
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.lc-summary-section {
  margin: 30px 0;
  padding: 20px;
  background-color: #e3f2fd;
  border-radius: 4px;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
}

.summary-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.summary-table td:last-child {
  text-align: right;
  font-weight: bold;
}

@media print {
  .print-container {
    box-shadow: none;
    padding: 20px;
  }

  .header {
    page-break-inside: avoid;
  }

  .pi-section {
    page-break-inside: avoid;
  }
}
</style>

